import { Chart as G2Chart } from '@antv/g2';
import { forwardRef, HTMLAttributes, Ref, useEffect, useImperativeHandle, useRef } from 'react';

import { IBaseChartProps, IComponent } from '../../../models/components.models';
import { useClassNames } from '../../../utils/hooks';
import { IRoseChartClassNames, IRoseChartStyles, roseChartStylesDefault } from './RoseChart.styles';

const render = (
  el: HTMLElement,
  data: { [key: string]: any }[],
  labelFieldName: string,
  valueFieldNames: string[],
  colors: string[] = []
) => {
  let style: { [key: string]: string } = {};
  if (colors[0]) {
    style = { fill: colors[0] };
  }
  const chart = new G2Chart({ container: el, autoFit: true });
  chart.coordinate({ type: 'polar' });
  chart
    .interval()
    .transform({ type: 'groupX', y: 'sum' })
    .data(data)
    .encode('x', labelFieldName)
    .encode('y', valueFieldNames[0])
    .scale('y', { type: 'sqrt' })
    .axis('y', { titleSpacing: 28, direction: 'right' })
    .animate('enter', { type: 'waveIn' })
    .tooltip({ channel: 'y' })
    .style(style);
  chart.render();
};

export interface IRoseChartProps extends IComponent<IRoseChartStyles>, HTMLAttributes<HTMLDivElement>, IBaseChartProps {
  colors?: string[];
}

export const RoseChart = forwardRef((props: IRoseChartProps, ref: Ref<HTMLDivElement>) => {
  // some variables are not used, but they need to get `elementProps`.
  const { className, styles, data = [], colors, labelFieldName = '', valueFieldNames = [], ...elementProps } = props;
  const refRoot = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => refRoot.current as HTMLDivElement);
  useEffect(() => {
    if (refRoot.current) {
      render(refRoot.current, data, labelFieldName, valueFieldNames, colors);
    }
  }, [data, labelFieldName, valueFieldNames, colors]);

  const classNames = useClassNames<Partial<IRoseChartStyles>, IRoseChartClassNames>(
    'RoseChart',
    roseChartStylesDefault,
    styles,
    className
  );
  return <div {...elementProps} ref={refRoot} className={classNames.root} />;
});
